import React from 'react';
import Card from '@/components/ui/Card';
import { TrainingMetrics } from '@/types';

interface TrainingMetricsWidgetProps {
  metrics: TrainingMetrics;
}

const TrainingMetricsWidget: React.FC<TrainingMetricsWidgetProps> = ({ metrics }) => {
  const getAttendanceColor = (attendance: number) => {
    if (attendance >= 90) return 'text-green-600 bg-green-100 dark:bg-green-900/20';
    if (attendance >= 75) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20';
    return 'text-red-600 bg-red-100 dark:bg-red-900/20';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatPercentage = (value: number) => `${Math.round(value)}%`;

  return (
    <Card className="h-full">
      <div className="p-4 h-full flex flex-col">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Training Overview</h3>
        
        {/* Weekly Attendance Highlight */}
        <div className={`rounded-lg p-4 mb-4 ${getAttendanceColor(metrics.weeklyAttendance)}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Weekly Attendance</p>
              <p className="text-2xl font-bold">{formatPercentage(metrics.weeklyAttendance)}</p>
            </div>
            <div className="text-right">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Avg Fitness</p>
                <p className={`text-lg font-semibold ${getScoreColor(metrics.averageFitness)}`}>
                  {Math.round(metrics.averageFitness)}/100
                </p>
              </div>
              <div className="text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, metrics.averageFitness)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400">Attitude Score</p>
                <p className={`text-lg font-semibold ${getScoreColor(metrics.attitudeScore)}`}>
                  {Math.round(metrics.attitudeScore)}/100
                </p>
              </div>
              <div className="text-purple-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.01M15 10h1.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="h-2 bg-purple-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(100, metrics.attitudeScore)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Session Stats */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Session Statistics</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <svg className="w-5 h-5 text-green-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{metrics.totalSessions}</p>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Sessions This Week</p>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <svg className="w-5 h-5 text-red-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{metrics.lateArrivals}</p>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Late Arrivals</p>
            </div>
          </div>
        </div>

        {/* Insights */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="space-y-2">
            {metrics.weeklyAttendance >= 95 && (
              <div className="flex items-center text-sm text-green-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Excellent attendance this week!
              </div>
            )}
            
            {metrics.lateArrivals > metrics.totalSessions * 0.2 && (
              <div className="flex items-center text-sm text-yellow-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                High rate of late arrivals this week
              </div>
            )}
            
            {metrics.averageFitness < 60 && (
              <div className="flex items-center text-sm text-red-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                Team fitness levels need attention
              </div>
            )}

            {metrics.attitudeScore >= 85 && (
              <div className="flex items-center text-sm text-green-600">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.01M15 10h1.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Great team attitude and morale!
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TrainingMetricsWidget;